import React, { useCallback } from 'react';
import { Header } from '@components/sources/source/warehouseSource/configureData/style';
import SourceConfigEditor, {
  IColumn,
} from '@components/sources/source/warehouseSource/configureData/sourceSpecificConfig';
import useStores from '@stores/useStores';
import { useConnectionState } from '../../visualDataMappingProvider';
import classes from '../mappingConfig/mapping-commons.module.scss';
import { generatePreview, getApiErrorMessageDetails, getRespData } from '../../sourceUtil';
import { fetchErrorAssets } from '../../errorsView';

interface SourceConnectionConfigProps {
  setLoading: (isLoading: boolean) => void;
}
const SourceConnectionConfig = ({ setLoading }: SourceConnectionConfigProps) => {
  const {
    ifUpdate,
    setColumns,
    columns,
    sourceConfig,
    setSourceConfig,
    setPreview,
    viewOnly,
    source,
    modelAccountId,
    updateSchemaErrors,
  } = useConnectionState();
  const { sourceDataStore, messagesStore } = useStores();
  const sourceDefName = source.sourceDef?.name || '';
  const skipSchemaConfiguration = sourceDefName?.toLowerCase() === 's3';
  const accountId =
    modelAccountId || source?.config?.accountId || source?.config?.rudderAccountId || '';
  const onChangeLoadingState = React.useCallback((loading) => {
    setLoading(loading);
  }, []);
  const config = {
    table: sourceConfig?.table?.name || '',
    ...(sourceConfig?.table || {}),
    sqlModelId: sourceConfig.sqlModelId,
  };
  const onChangeColumns = (cols: IColumn[] | string[]) => {
    const newCols = cols as IColumn[];
    setColumns(newCols);
  };
  const options = {
    schema: config?.schema,
    table: config?.name,
    bucketName: config?.bucketName,
    prefix: config?.prefix,
    accountId,
    sqlModelId: config.sqlModelId,
  };
  const getPreviewData = useCallback(async () => {
    try {
      const previewOrError = await sourceDataStore.getPreview(accountId, sourceDefName, options);
      const { rows } = getRespData(previewOrError);
      setPreview(generatePreview(rows, columns));
    } catch (error) {
      const errorDetails = getApiErrorMessageDetails(error, 'Could not fetch preview');
      updateSchemaErrors([fetchErrorAssets('preview', errorDetails.message)]);
      messagesStore?.showErrorMessage(errorDetails.messageWithFallback);
    }
  }, [columns]);

  React.useEffect(() => {
    if (
      !(
        columns?.length &&
        ((options.schema && options.table) || options.bucketName || options.sqlModelId)
      )
    ) {
      return;
    }
    getPreviewData();
  }, [getPreviewData]);

  const onChangeConfig = !skipSchemaConfiguration
    ? (newConfig: { schema?: string; table?: string; sqlModelId?: string; accountId?: string }) => {
        const tableName = newConfig?.table;
        const tableSchema = newConfig?.schema || sourceConfig?.table?.schema;
        const updatedSourceConfig = newConfig.sqlModelId
          ? { sqlModelId: newConfig.sqlModelId, table: {} }
          : {
              table: {
                schema: tableSchema,
                name: tableName,
              },
              sqlModelId: undefined,
            };
        setSourceConfig(updatedSourceConfig, false, newConfig.accountId);
      }
    : (newConfig: { bucketName?: string; prefix?: string }) => {
        const bucket = Object.prototype.hasOwnProperty.call(newConfig, 'bucketName')
          ? newConfig.bucketName
          : sourceConfig.table?.bucketName;
        const pre = Object.prototype.hasOwnProperty.call(newConfig, 'prefix')
          ? newConfig.prefix
          : sourceConfig?.table?.prefix;
        const updatedSourceConfig = {
          table: {
            bucketName: bucket,
            prefix: pre,
          },
        };
        setSourceConfig(updatedSourceConfig);
      };
  if (!source.sourceDef) {
    return null;
  }
  return (
    <>
      <div className={classes.head_wrapper}>
        <Header>What data do you want to sync?</Header>
      </div>

      <SourceConfigEditor
        onChangeConfig={onChangeConfig}
        mappedToDestination
        onChangeLoadingState={onChangeLoadingState}
        onChangeColumns={onChangeColumns}
        sourceDefinition={source.sourceDef}
        accountId={accountId}
        defaultValue={config}
        config={config}
        isViewOrUpdate={Boolean(ifUpdate || viewOnly)}
        columnsAsObject
        updateSchemaErrors={updateSchemaErrors}
        skipSchemaConfiguration={skipSchemaConfiguration}
      />
    </>
  );
};

export default SourceConnectionConfig;
